/**
 * The token-level facts every repair rule needs, computed once per rule call, in one place.
 *
 * ADR-0011 forbids regexes over `ctx.source`, because a regex cannot answer "am I inside a
 * string literal?" — the question that decides whether a `//`, a `,}` or a `{a: 1}` is a
 * defect or somebody's data. This module is the positive form of that rule: every helper
 * here takes tokens, and the only text it ever looks at is `source.slice(token.from,
 * token.to)` — a slice whose boundaries the scanner drew, so "inside a string" is a fact
 * and not a guess.
 *
 * DEVIATION from docs/ARCHITECTURE.md §3, which lists `repair/` as `types.ts`,
 * `registry.ts`, `engine.ts` and `rules/`. Five rules (`remove-trailing-comma`,
 * `remove-stray-comma`, `insert-missing-comma`, `insert-missing-colon`,
 * `close-open-containers`, `drop-extra-closers`) all need the same answer — "which
 * container am I in, and what is the grammar expecting here?" — and copying a container
 * scanner into six files is exactly the kind of drift the rule registry exists to prevent.
 * `rules/` stays one-rule-per-file as specified; this file sits beside `engine.ts`.
 */

import { significantTokens } from '../json/lexer';
import { isTrivia, ScanErrorKind, type Token, TokenKind } from '../json/tokens';
import type { Span } from '../lint/diagnostic';

/** `source.slice(token.from, token.to)` — the only sanctioned way to read a token's text. */
export function textOf(source: string, token: Token): string {
  return source.slice(token.from, token.to);
}

/** First code unit of `token`, or `''` for the zero-width EOF sentinel. */
export function firstCharOf(source: string, token: Token): string {
  return token.to > token.from ? source.charAt(token.from) : '';
}

export const isOpener = (token: Token): boolean =>
  token.kind === TokenKind.OpenBrace || token.kind === TokenKind.OpenBracket;

export const isCloser = (token: Token): boolean =>
  token.kind === TokenKind.CloseBrace || token.kind === TokenKind.CloseBracket;

/**
 * Kinds that can begin a value. `Unknown` is included because a broken document is full of
 * them (`NaN`, `'a'`, `“a”`, `+1`) and the container scan has to stay in step with the
 * grammar rather than derail on the first unrecognised word.
 */
const VALUE_START: ReadonlySet<TokenKind> = new Set<TokenKind>([
  TokenKind.String,
  TokenKind.Number,
  TokenKind.True,
  TokenKind.False,
  TokenKind.Null,
  TokenKind.OpenBrace,
  TokenKind.OpenBracket,
  TokenKind.Unknown,
]);

/**
 * Kinds that unambiguously *finish* a value, and the strict half of the missing-comma test.
 *
 * `Unknown` is deliberately absent: `-Infinity` scans as two adjacent `Unknown` tokens, and
 * a rule that took "value, then value" literally there would insert a comma into the middle
 * of a single literal. Requiring both sides to be unambiguous is what keeps
 * `insert-missing-comma` off ambiguous ground.
 */
const VALUE_END: ReadonlySet<TokenKind> = new Set<TokenKind>([
  TokenKind.String,
  TokenKind.Number,
  TokenKind.True,
  TokenKind.False,
  TokenKind.Null,
  TokenKind.CloseBrace,
  TokenKind.CloseBracket,
]);

const STRICT_VALUE_START: ReadonlySet<TokenKind> = new Set<TokenKind>([
  TokenKind.String,
  TokenKind.Number,
  TokenKind.True,
  TokenKind.False,
  TokenKind.Null,
  TokenKind.OpenBrace,
  TokenKind.OpenBracket,
]);

export const startsValue = (token: Token): boolean => VALUE_START.has(token.kind);
export const endsValue = (token: Token): boolean => VALUE_END.has(token.kind);
export const startsValueStrictly = (token: Token): boolean => STRICT_VALUE_START.has(token.kind);

/**
 * A forward-only "which span covers this offset?" probe over spans that are sorted by
 * `from` and do not overlap each other.
 *
 * The returned function must be called with non-decreasing offsets — every caller below
 * walks the token stream left to right, so that holds by construction. Each call advances a
 * private cursor and never rewinds, which makes a whole sweep O(tokens + spans) instead of
 * the O(tokens × spans) that a `spans.find(...)` inside the loop costs.
 *
 * This is not a micro-optimisation. `computeStructuralTokens` used `literals.find(...)` and
 * `comments.some(...)` per token, so a single-quoted paste — a Python `repr()` or a JS
 * object literal, the single most common broken input this engine exists to accept — cost
 * quadratic time: a 265 KB document spent 2.6 s of a 3.2 s lint inside that one function,
 * on the main thread, inside the paste transaction.
 */
function spanCursor<T extends { readonly from: number; readonly to: number }>(
  spans: readonly T[],
): (offset: number) => T | undefined {
  let index = 0;
  return (offset: number): T | undefined => {
    while (index < spans.length) {
      const span = spans[index];
      if (span === undefined || span.to > offset) break;
      index += 1;
    }
    const span = spans[index];
    return span !== undefined && offset >= span.from ? span : undefined;
  };
}

/**
 * The two kinds of span that make a region of the source opaque to the grammar: a
 * `#`-to-end-of-line comment, and a string literal written with delimiters JSON does not
 * accept.
 *
 * They are found in ONE left-to-right pass, and that is the point. They used to be two
 * passes, with the hash scan running first and the literal scan consulting its output —
 * which made the hash scan authoritative over text it had no business claiming. `{'a': 'x #
 * y', 'b': 2}` was the consequence: the `#` inside the user's string opened a "comment" that
 * swallowed the rest of the document, `'b'` was never recognised as a literal, and the
 * repair returned `{"a": 'x }`. Reading left to right, whichever construct *opens* first
 * consumes its own extent and the other one never sees the inside of it — which is the same
 * rule the scanner itself follows for `"` and `//`.
 */
interface OpaqueSpans {
  readonly hashComments: readonly Span[];
  readonly foreign: readonly ForeignLiteral[];
}

function computeOpaqueSpans(source: string, tokens: readonly Token[]): OpaqueSpans {
  const hashComments: Span[] = [];
  const foreign: ForeignLiteral[] = [];
  let consumedTo = -1;

  for (let i = 0; i < tokens.length; i += 1) {
    const token = tokens[i];
    if (token === undefined) continue;
    if (token.kind !== TokenKind.Unknown) continue;
    if (token.from < consumedTo) continue;

    const opener = firstCharOf(source, token);

    if (opener === '#') {
      let to = source.length;
      for (let j = i + 1; j < tokens.length; j += 1) {
        const next = tokens[j];
        if (next !== undefined && next.kind === TokenKind.LineBreak) {
          to = next.from;
          break;
        }
      }
      hashComments.push({ from: token.from, to });
      consumedTo = to;
      continue;
    }

    const pair = FOREIGN_QUOTES.find(([open]) => open === opener);
    if (pair === undefined) continue;

    const literal = readDelimited(source, token.from, pair[0], pair[1]);
    if (literal === undefined) continue;

    consumedTo = literal.to;
    foreign.push({ from: literal.from, to: literal.to, open: pair[0], value: literal.value });
  }

  return { hashComments, foreign };
}

/**
 * `#`-to-end-of-line comments.
 *
 * `jsonc-parser` has no notion of them — `{"a":1} # done` scans as `Unknown('#')`,
 * `Whitespace`, `Unknown('done')` — so the span is reconstructed from the `#` token to the
 * next line break. The scan is still token-anchored: a `#` inside a JSON string literal
 * lives inside a `String` token and is never looked at, and a `#` inside a *foreign* string
 * literal is skipped because {@link computeOpaqueSpans} reaches the literal's opening quote
 * first.
 */
export function hashCommentSpans(source: string, tokens: readonly Token[]): readonly Span[] {
  const slot = slotFor(source, tokens);
  slot.opaque ??= computeOpaqueSpans(source, tokens);
  return slot.opaque.hashComments;
}

/** Opening delimiter → closing delimiter, for the string literals JSON does not have. */
const FOREIGN_QUOTES: ReadonlyArray<readonly [string, string]> = [
  ["'", "'"],
  ['“', '”'], // “ ”
  ['‘', '’'], // ‘ ’
];

/** A string literal written with delimiters JSON does not accept. */
export interface ForeignLiteral {
  readonly from: number;
  readonly to: number;
  /** The opening delimiter, which says which rule owns the repair. */
  readonly open: string;
  /** The decoded value, ready for `JSON.stringify`. */
  readonly value: string;
}

/** A `U+FEFF` at offset 0, which jsonc reports as `Unknown@0+1` (`json/lexer.ts`). */
function hasLeadingBom(source: string, tokens: readonly Token[]): boolean {
  const first = tokens[0];
  return (
    first !== undefined &&
    first.kind === TokenKind.Unknown &&
    first.from === 0 &&
    first.to === 1 &&
    source.charAt(0) === '\uFEFF'
  );
}

/**
 * Significant tokens (trivia dropped, EOF sentinel kept), with `#`-comment bodies removed,
 * every foreign string literal collapsed into one opaque token, and a leading BOM dropped.
 *
 * This is the stream the grammar actually runs on. Each step exists because of a defect it
 * prevents: without the first, the container scan reads the `"a"` in `# note: "a": 1` as a
 * property name; without the second, it reads the middle of `'he said "hi"'` as one; without
 * the third, `U+FEFF{"a":1}` has the BOM as its root value and the user's entire document as
 * *trailing content*, which `drop-trailing-content` then offers to delete — `repair` returned
 * the empty string for a document whose only defect was its encoding.
 *
 * The BOM is dropped here rather than special-cased in {@link VALUE_START}: `Unknown` really
 * does start a value (`NaN`, `'a'`, `+1`) and the scan has to stay in step with the grammar.
 * A byte-order mark is not a value at all, which is the one thing that distinguishes it —
 * and it is a fact about offset 0, not about the token kind.
 */
function computeStructuralTokens(source: string, tokens: readonly Token[]): readonly Token[] {
  const { hashComments, foreign } = opaqueSpans(source, tokens);
  const significant = significantTokens(tokens);
  const bom = hasLeadingBom(source, tokens);
  if (hashComments.length === 0 && foreign.length === 0 && !bom) return significant;

  const commentAt = spanCursor(hashComments);
  const literalAt = spanCursor(foreign);
  const out: Token[] = [];
  let emittedTo = -1;

  for (const token of significant) {
    if (token.kind === TokenKind.EOF) {
      out.push(token);
      continue;
    }

    if (bom && token.from === 0 && token.to === 1) continue;
    if (commentAt(token.from) !== undefined) continue;

    const literal = literalAt(token.from);
    if (literal === undefined) {
      out.push(token);
      continue;
    }

    if (literal.to <= emittedTo) continue;
    emittedTo = literal.to;
    out.push({
      kind: TokenKind.Unknown,
      from: literal.from,
      to: literal.to,
      scanError: ScanErrorKind.None,
    });
  }

  return out;
}

/**
 * Offset at which the structural scan stops trusting the token stream, or `undefined`.
 *
 * A raw line break inside a string literal desynchronises everything after it: `{"msg":"line1
 * ⏎ line2"}` scans as `String("line1` (unterminated), `Unknown(line2)`, `String("}`
 * (unterminated) — so the fragments of the user's sentence land in property-name and value
 * position, and three rules that are individually correct compose into
 * `{"msg":"line1,⏎"line2":"}"}`: five repairs reported, the member `"b":2` renamed to
 * `":b":2`, and a document that still does not parse and is now harder to fix by hand.
 *
 * `close-unterminated-string` already refuses to guess here — its header names this exact
 * input shape — but restraint in one rule cannot hold a property of the composition. The
 * scan stops instead, so every rule that reads it sees nothing to propose past this point.
 * Whatever was recorded BEFORE the break is still fact and is still repaired.
 *
 * A string left unterminated because the DOCUMENT ran out is a different case entirely — the
 * end of the literal is not a guess there — so only a break with real content after it
 * counts.
 */
/**
 * Offset at which collapsing a foreign literal stops being sound, or `undefined`.
 *
 * Collapsing `'…'` into one opaque token works because the scanner's boundaries and the
 * literal's boundaries agree — except when they do not. `{“u”: “http://x.com/a”}` scans the
 * `//` as a `LineComment` that runs to the end of the document, so the literal's closing
 * `”` AND the document's `}` both live inside a token the collapse cannot split. The `}`
 * is trivia to `significantTokens`, so it vanishes, and `close-open-containers` cheerfully
 * appends a second one: one repair reported, document still invalid.
 *
 * The structure scan stops here instead. Nothing is lost: the two quote rules read
 * {@link findForeignLiterals} directly rather than the collapsed stream, so they still
 * re-quote the literal on this pass, and the NEXT pass sees a document whose `//` is inside
 * a real `String` token and scans it cleanly.
 */
function straddleOffset(source: string, tokens: readonly Token[]): number | undefined {
  const { foreign } = opaqueSpans(source, tokens);
  if (foreign.length === 0) return undefined;

  const literalAt = spanCursor(foreign);
  for (const token of tokens) {
    const literal = literalAt(token.from);
    if (literal === undefined) continue;
    if (literal.to > token.from && literal.to < token.to) return literal.to;
  }
  return undefined;
}

export function desyncOffset(tokens: readonly Token[]): number | undefined {
  for (let i = 0; i < tokens.length; i += 1) {
    const token = tokens[i];
    if (token === undefined) continue;
    if (token.kind !== TokenKind.String) continue;
    if (token.scanError !== ScanErrorKind.UnterminatedString) continue;

    for (let j = i + 1; j < tokens.length; j += 1) {
      const next = tokens[j];
      if (next === undefined || isTrivia(next)) continue;
      if (next.kind === TokenKind.EOF) return undefined;
      return token.to;
    }
    return undefined;
  }
  return undefined;
}

export type ContainerKind = 'object' | 'array';

export interface OpenFrame {
  readonly kind: ContainerKind;
  /** The `{` or `[` that opened it. */
  readonly open: Token;
}

/** Two adjacent significant tokens with a separator missing between them. */
export interface MissingSeparator {
  readonly after: Token;
  readonly before: Token;
}

/**
 * Containers that are still open at one offset, innermost first — which is the order their
 * closers have to be written in. A counter cannot express this; that is why the legacy
 * fixer turned `{"a":[1` into `{"a":[1}]`.
 */
export interface UnclosedGroup {
  /** Offset at which the missing closers belong. */
  readonly at: number;
  readonly frames: readonly OpenFrame[];
}

export interface StructureScan {
  /** Tokens consumed in property-name position. */
  readonly keyTokens: ReadonlySet<Token>;
  /**
   * The subset of {@link keyTokens} directly followed by a `:`.
   *
   * "It sits where a key goes" is a recovery guess; "it sits where a key goes AND a colon
   * follows it" is a fact. `{"a":0x1F}` is why the distinction matters: the recovery reads
   * the `x1F` of the hex literal as a property name, and quoting it would produce
   * `{"a":0"x1F"}` instead of `{"a":31}`.
   */
  readonly namedKeys: ReadonlySet<Token>;
  /** Tokens consumed in value position. */
  readonly valueTokens: ReadonlySet<Token>;
  readonly missingComma: readonly MissingSeparator[];
  readonly missingColon: readonly MissingSeparator[];
  readonly unclosed: readonly UnclosedGroup[];
  /** Closers past the end of a complete root value: `{"a":1}}`. */
  readonly extraClosers: readonly Token[];
  /** Significant tokens after the root value, excluding {@link extraClosers}. */
  readonly trailingContent: readonly Token[];
  /** A top-level `,` with nothing but trivia behind it: `{"a":1}  ,  `. */
  readonly danglingCommas: readonly Token[];
  /** The `'…'` / `“…”` literals, for the two rules that re-quote them. */
  readonly foreign: readonly ForeignLiteral[];
}

type ObjectPhase = 'key' | 'colon' | 'value' | 'comma';
type ArrayPhase = 'value' | 'comma';

interface Frame {
  readonly kind: ContainerKind;
  readonly open: Token;
  phase: ObjectPhase | ArrayPhase;
}

/**
 * One tolerant left-to-right walk of the token stream that answers every structural
 * question the rules ask.
 *
 * It is a *recovering* scan, not a parser: it never throws, never gives up, and when the
 * grammar is violated it records the violation and carries on in the state the author
 * most likely meant. That is what lets six independent rules read one consistent picture
 * of a document that does not parse.
 */
/**
 * Two `Number` tokens with nothing between them are one number literal, not two values.
 *
 * `jsonc-parser` splits `007` into `Number('0')`, `Number('0')`, `Number('7')` — the same
 * split `fix-number-literal` reads to rewrite `007` → `7`, and `printer.ts` repeats the
 * reasoning. The structure scan did not, so it recorded a missing comma between each pair
 * and `[007]` was "repaired" into `[0,0,7]`: valid JSON, a different array length, different
 * values, the pill reading *Valid JSON* and the toast reporting two successful repairs.
 * `insert-missing-comma` (25) and `insert-missing-colon` (26) outrank `fix-number-literal`
 * (37), so the rule that exists to fix this never got to run.
 *
 * Adjacency is the whole test, and it is a fact about tokens rather than a guess about
 * spacing: `[0 0 7]` really is three values and still gets its commas.
 */
/** The earlier of two optional offsets. */
function firstOf(a: number | undefined, b: number | undefined): number | undefined {
  if (a === undefined) return b;
  if (b === undefined) return a;
  return Math.min(a, b);
}

function continuesNumber(previous: Token | undefined, token: Token): boolean {
  return (
    previous !== undefined &&
    previous.kind === TokenKind.Number &&
    token.kind === TokenKind.Number &&
    previous.to === token.from
  );
}

function computeStructure(source: string, tokens: readonly Token[]): StructureScan {
  const sig = structuralTokens(source, tokens);
  const desyncAt = firstOf(desyncOffset(tokens), straddleOffset(source, tokens));

  const keyTokens = new Set<Token>();
  const namedKeys = new Set<Token>();
  const valueTokens = new Set<Token>();
  const missingComma: MissingSeparator[] = [];
  const missingColon: MissingSeparator[] = [];
  const unclosed: UnclosedGroup[] = [];
  const extraClosers: Token[] = [];
  const trailingContent: Token[] = [];
  const danglingCommas: Token[] = [];

  const stack: Frame[] = [];
  let rootDone = false;
  let everOpened = false;
  /** True while the only things seen since the root closed are further closers. */
  let closerRun = false;
  let previous: Token | undefined;

  const push = (token: Token): void => {
    everOpened = true;
    stack.push({
      kind: token.kind === TokenKind.OpenBrace ? 'object' : 'array',
      open: token,
      phase: token.kind === TokenKind.OpenBrace ? 'key' : 'value',
    });
  };

  const pop = (): void => {
    stack.pop();
    if (stack.length === 0) {
      rootDone = true;
      closerRun = true;
    }
  };

  /**
   * A closer that does not match the innermost frame. When the stack holds a frame of the
   * closer's kind, everything above it was never closed and its closers belong right here —
   * `{"a":[1}` needs `]` before the `}`, which is the case a depth counter gets wrong.
   */
  const closeMismatched = (token: Token): void => {
    const wanted: ContainerKind = token.kind === TokenKind.CloseBrace ? 'object' : 'array';
    let target = -1;
    for (let i = stack.length - 1; i >= 0; i -= 1) {
      if (stack[i]?.kind === wanted) {
        target = i;
        break;
      }
    }
    if (target === -1) return; // No frame it could belong to: not ours to repair.

    const orphans: OpenFrame[] = [];
    for (let i = stack.length - 1; i > target; i -= 1) {
      const frame = stack[i];
      if (frame !== undefined) orphans.push({ kind: frame.kind, open: frame.open });
    }
    if (orphans.length > 0) unclosed.push({ at: token.from, frames: orphans });

    stack.length = target;
    if (stack.length === 0) {
      rootDone = true;
      closerRun = true;
    }
  };

  for (let i = 0; i < sig.length; i += 1) {
    const token = sig[i];
    if (token === undefined) continue;
    if (token.kind === TokenKind.EOF) break;

    // Past the desync point nothing downstream is trustworthy — see `desyncOffset` and
    // `straddleOffset`. Stop, and report only what was established before it.
    if (desyncAt !== undefined && token.from >= desyncAt) break;

    // A token is re-processed at most once, after a missing separator has been recorded and
    // the phase advanced. The bound makes termination a property of the loop.
    for (let attempt = 0; attempt < 2; attempt += 1) {
      const top = stack[stack.length - 1];
      let reprocess = false;

      if (top === undefined) {
        if (isCloser(token)) {
          if (everOpened && rootDone && closerRun) extraClosers.push(token);
          else if (rootDone) trailingContent.push(token);
        } else if (rootDone) {
          closerRun = false;
          if (token.kind === TokenKind.Comma && sig[i + 1]?.kind === TokenKind.EOF) {
            danglingCommas.push(token);
          } else {
            trailingContent.push(token);
          }
        } else if (isOpener(token)) {
          push(token);
        } else if (startsValue(token)) {
          valueTokens.add(token);
          rootDone = true;
          closerRun = true;
        }
      } else if (top.kind === 'object') {
        switch (top.phase) {
          case 'key':
            if (token.kind === TokenKind.CloseBrace) pop();
            else if (isCloser(token)) closeMismatched(token);
            else if (token.kind === TokenKind.Comma || token.kind === TokenKind.Colon) break;
            else if (isOpener(token)) {
              top.phase = 'comma';
              push(token);
            } else {
              keyTokens.add(token);
              if (sig[i + 1]?.kind === TokenKind.Colon) namedKeys.add(token);
              top.phase = 'colon';
            }
            break;

          case 'colon':
            if (token.kind === TokenKind.Colon) top.phase = 'value';
            else if (token.kind === TokenKind.CloseBrace) pop();
            else if (isCloser(token)) closeMismatched(token);
            else if (token.kind === TokenKind.Comma) top.phase = 'key';
            else if (continuesNumber(previous, token)) break;
            else if (startsValue(token)) {
              if (previous !== undefined) missingColon.push({ after: previous, before: token });
              top.phase = 'value';
              reprocess = true;
            }
            break;

          case 'value':
            if (token.kind === TokenKind.CloseBrace) pop();
            else if (isCloser(token)) closeMismatched(token);
            else if (token.kind === TokenKind.Comma) top.phase = 'key';
            else if (isOpener(token)) {
              top.phase = 'comma';
              push(token);
            } else if (startsValue(token)) {
              valueTokens.add(token);
              top.phase = 'comma';
            }
            break;

          case 'comma':
            if (token.kind === TokenKind.Comma) top.phase = 'key';
            else if (token.kind === TokenKind.CloseBrace) pop();
            else if (isCloser(token)) closeMismatched(token);
            else if (continuesNumber(previous, token)) break;
            else {
              // Inside an object the next thing after a member is a property NAME, so the
              // only unambiguous missing-comma site is `value "key"`. `{"a":1 [2]}` is a
              // different defect and inserting a comma there would not make it parse.
              if (
                previous !== undefined &&
                endsValue(previous) &&
                token.kind === TokenKind.String
              ) {
                missingComma.push({ after: previous, before: token });
              }
              top.phase = 'key';
              reprocess = true;
            }
            break;
        }
      } else {
        switch (top.phase) {
          case 'value':
            if (token.kind === TokenKind.CloseBracket) pop();
            else if (isCloser(token)) closeMismatched(token);
            else if (token.kind === TokenKind.Comma || token.kind === TokenKind.Colon) break;
            else if (isOpener(token)) {
              top.phase = 'comma';
              push(token);
            } else {
              valueTokens.add(token);
              top.phase = 'comma';
            }
            break;

          case 'comma':
            if (token.kind === TokenKind.Comma) top.phase = 'value';
            else if (token.kind === TokenKind.CloseBracket) pop();
            else if (isCloser(token)) closeMismatched(token);
            else if (continuesNumber(previous, token)) break;
            else {
              if (previous !== undefined && endsValue(previous) && startsValueStrictly(token)) {
                missingComma.push({ after: previous, before: token });
              }
              top.phase = 'value';
              reprocess = true;
            }
            break;
        }
      }

      if (!reprocess) break;
    }

    previous = token;
  }

  // Containers left open in a document the scan could not read to the end are not
  // "unclosed" — they are unknown. Closing them appends a bracket after a line break the
  // author meant as data, or a second `}` onto a document that already has one.
  if (stack.length > 0 && desyncAt === undefined) {
    const frames: OpenFrame[] = [];
    for (let i = stack.length - 1; i >= 0; i -= 1) {
      const frame = stack[i];
      if (frame !== undefined) frames.push({ kind: frame.kind, open: frame.open });
    }
    unclosed.push({ at: source.length, frames });
  }

  return {
    keyTokens,
    namedKeys,
    valueTokens,
    missingComma,
    missingColon,
    unclosed,
    extraClosers,
    trailingContent,
    danglingCommas,
    foreign: findForeignLiterals(source, tokens),
  };
}

/**
 * Decode one JS/JSON escape body starting at `index` (the code unit after the backslash).
 * Returns the decoded text and the index just past the escape.
 *
 * Used by the two quote rules, which have to re-emit a foreign literal as a JSON one:
 * `'he said "hi"'` must become `"he said \"hi\""`, not `"he said "hi""` — the exact
 * transformation the legacy `single-to-double-quotes` got wrong.
 */
function decodeEscape(source: string, index: number): { text: string; next: number } {
  const char = source.charAt(index);

  switch (char) {
    case 'n':
      return { text: '\n', next: index + 1 };
    case 't':
      return { text: '\t', next: index + 1 };
    case 'r':
      return { text: '\r', next: index + 1 };
    case 'b':
      return { text: '\b', next: index + 1 };
    case 'f':
      return { text: '\f', next: index + 1 };
    case 'v':
      return { text: '\v', next: index + 1 };
    case '0':
      return { text: '\0', next: index + 1 };
    case 'u': {
      const hex = source.slice(index + 1, index + 5);
      if (hex.length === 4 && isHex(hex)) {
        return { text: String.fromCharCode(Number.parseInt(hex, 16)), next: index + 5 };
      }
      return { text: 'u', next: index + 1 };
    }
    case 'x': {
      const hex = source.slice(index + 1, index + 3);
      if (hex.length === 2 && isHex(hex)) {
        return { text: String.fromCharCode(Number.parseInt(hex, 16)), next: index + 3 };
      }
      return { text: 'x', next: index + 1 };
    }
    default:
      // `\'`, `\"`, `\\`, `\/` and anything else: the character stands for itself.
      return { text: char, next: index + 1 };
  }
}

export function isHex(text: string): boolean {
  if (text.length === 0) return false;
  for (const char of text) {
    const code = char.charCodeAt(0);
    const hex =
      (code >= 0x30 && code <= 0x39) ||
      (code >= 0x41 && code <= 0x46) ||
      (code >= 0x61 && code <= 0x66);
    if (!hex) return false;
  }
  return true;
}

/**
 * True when `text` carries nothing a reader could see: whitespace, line terminators, and the
 * zero-width family (`U+FEFF`, `U+200B`–`U+200F`, `U+2060`) that `String.prototype.trim`
 * does not touch.
 *
 * No regex — ADR-0011 keeps `repair/` free of them, and this is the predicate a regex would
 * have been reached for.
 */
export function isBlankOrInvisible(text: string): boolean {
  if (text.trim().length === 0) return true;
  for (const char of text) {
    const code = char.codePointAt(0) ?? 0;
    const invisible =
      (code >= 0x200b && code <= 0x200f) || code === 0x2060 || code === 0xfeff || code === 0x00ad;
    if (!invisible && char.trim().length > 0) return false;
  }
  return true;
}

export function isDigit(char: string): boolean {
  const code = char.charCodeAt(0);
  return code >= 0x30 && code <= 0x39;
}

/**
 * A foreign string literal found in the token stream: where it starts, where it ends and
 * what it means.
 */
export interface ForeignString {
  readonly from: number;
  readonly to: number;
  /** The decoded value, ready for `JSON.stringify`. */
  readonly value: string;
}

/**
 * Read a literal delimited by `open` … `close` starting at `from`, honouring backslash
 * escapes and stopping at a line break.
 *
 * `from` must be the first code unit of a token the scanner did NOT classify as a string —
 * that is what makes the whole span provably outside any JSON string literal, and it is why
 * this can safely read across the `String` token that `'he said "hi"'` contains in its
 * middle. Returns `undefined` when the literal is not closed on its line.
 */
export function readDelimited(
  source: string,
  from: number,
  open: string,
  close: string,
): ForeignString | undefined {
  if (source.charAt(from) !== open) return undefined;

  let index = from + 1;
  let value = '';

  while (index < source.length) {
    const char = source.charAt(index);

    if (char === '\n' || char === '\r') return undefined;

    if (char === '\\') {
      const decoded = decodeEscape(source, index + 1);
      value += decoded.text;
      index = decoded.next;
      continue;
    }

    if (char === close) return { from, to: index + 1, value };

    value += char;
    index += 1;
  }

  return undefined;
}

// ── memoisation ───────────────────────────────────────────────────────────────────────────

/**
 * A one-entry cache in front of the three scans above, keyed on the IDENTITY of the token
 * array (plus the source, cheaply, by reference).
 *
 * WHY IT IS HERE. Every rule is a pure function of `(source, tokens)` and every rule needs
 * the same structural picture, so the eighteen of them re-derived it eighteen times. That
 * was fine at the scale the rules were tested on and quadratic-feeling at the scale the
 * product ships: measured on the 2 MiB perf fixture with ONE stray comma in it, binding
 * quick fixes cost 850 ms spread almost evenly across seventeen rules — 48 ms each,
 * essentially all of it the same walk, seventeen times. With this cache the same document
 * costs one walk. The repair driver benefits twice over, since it does this per pass.
 *
 * ONE entry, not a map: within a pass every rule is handed the *same frozen context*, so the
 * key is the same array reference and a single slot hits every time. A cache that outlived
 * the pass would be a memory leak holding a megabyte of the user's document; this one is
 * replaced the moment a different document is analysed.
 *
 * Correctness rests on two facts that are worth stating because they are what makes an
 * identity key sound here: `tokenize()` allocates a fresh array per call, so a token array
 * identifies exactly one analysis of one text; and every value cached below is treated as
 * read-only by its callers, which the `readonly` return types now make a compile error to
 * violate rather than a convention to remember.
 */
interface ScanCache {
  readonly source: string;
  readonly tokens: readonly Token[];
  opaque?: OpaqueSpans;
  structural?: readonly Token[];
  structure?: StructureScan;
}

let cache: ScanCache | undefined;

function slotFor(source: string, tokens: readonly Token[]): ScanCache {
  if (cache !== undefined && cache.tokens === tokens && cache.source === source) return cache;
  cache = { source, tokens };
  return cache;
}

/** The `#` comments and foreign literals of this document, memoised. */
function opaqueSpans(source: string, tokens: readonly Token[]): OpaqueSpans {
  const slot = slotFor(source, tokens);
  slot.opaque ??= computeOpaqueSpans(source, tokens);
  return slot.opaque;
}

/**
 * The `'…'` / `“…”` literals of this document — see {@link computeOpaqueSpans}, which this
 * memoises.
 *
 * The result is what makes the *interior* of such a literal invisible to every other rule.
 * Without it, `{'a':'he said "hi"'}` would show `said` in property-name position and `"hi"`
 * as its value, and four rules would happily rewrite somebody's sentence.
 */
export function findForeignLiterals(
  source: string,
  tokens: readonly Token[],
): readonly ForeignLiteral[] {
  return opaqueSpans(source, tokens).foreign;
}

/**
 * True when `offset` falls inside a `#` comment or a foreign string literal — i.e. inside
 * text the grammar must not read.
 *
 * `remove-comment` is the caller, and it is the one rule that cannot get this from
 * {@link structuralTokens}: it works on the RAW stream, because a `//` or `/* *\/` really is
 * a comment token there, and the collapsed stream has already thrown those away. Which is
 * exactly how `{'url': 'http://example.com/a'}` used to lose everything from the `//`
 * onwards — the `LineComment` token jsonc produces inside a single-quoted literal was
 * deleted as a comment, reproducing the legacy engine's worst defect on the single most
 * common non-JSON paste this tool exists to accept.
 */
export function isInsideOpaqueSpan(
  source: string,
  tokens: readonly Token[],
  offset: number,
): boolean {
  const { hashComments, foreign } = opaqueSpans(source, tokens);
  return covers(hashComments, offset) || covers(foreign, offset);
}

function covers(spans: readonly Span[], offset: number): boolean {
  let low = 0;
  let high = spans.length;
  while (low < high) {
    const mid = (low + high) >>> 1;
    const span = spans[mid];
    if (span === undefined) break;
    if (span.to <= offset) low = mid + 1;
    else high = mid;
  }
  const span = spans[low];
  return span !== undefined && offset >= span.from && offset < span.to;
}

/** The stream the grammar runs on — see {@link computeStructuralTokens}, memoised. */
export function structuralTokens(source: string, tokens: readonly Token[]): readonly Token[] {
  const slot = slotFor(source, tokens);
  slot.structural ??= computeStructuralTokens(source, tokens);
  return slot.structural;
}

/** Every structural question a rule asks — see {@link computeStructure}, memoised. */
export function scanStructure(source: string, tokens: readonly Token[]): StructureScan {
  const slot = slotFor(source, tokens);
  slot.structure ??= computeStructure(source, tokens);
  return slot.structure;
}
